/**
 * Typed bindings for /api/accounts.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { ApiError, api, extractErrorDetail } from "./api";

export type AccountStatus =
  | "active"
  | "flood_wait"
  | "banned"
  | "auth_required"
  | "paused"
  | "warming_up"
  // Staggered import: saved offline, first Telegram connect still queued.
  | "pending_verify";

/**
 * Derived status — shown in the UI instead of the storage status when the
 * row needs to call out a specific failure mode that the storage status
 * doesn't capture:
 *   - "frozen"     — TG antifraud locked the account (FrozenMethodInvalid).
 *                    Storage status may still be `active` if the freeze
 *                    happened during warmup before the test endpoint caught it.
 *   - "proxy_dead" — the attached proxy failed its last health check OR the
 *                    last account-test errored with a network-level failure.
 *                    The account itself may be fine — the operator needs to
 *                    fix the proxy, not the account.
 */
export type DerivedStatus = AccountStatus | "frozen" | "proxy_dead";

export type AccountRole = "inviter" | "scraper" | "both" | "demoted_scraper";

export interface AccountSummary {
  id: string;
  // Short per-company number shown as "#064"; assigned once, never reused.
  seq_no: number;
  tg_user_id: number | null;
  tg_username: string | null;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  is_premium: boolean;
  status: AccountStatus;
  status_reason: string | null;
  role: AccountRole;
  proxy_id: string | null;
  proxy_label: string | null;
  proxy_is_alive: boolean | null;
  proxy_coherence_warning?: string | null;
  proxy_capacity_warning?: string | null;
  recommended_proxy_type?: string | null;
  health_score?: number;
  health_breakdown?: {
    bans: number;
    profile: number;
    activity: number;
    age: number;
    session: number;
  } | null;
  api_credential_id: string;
  api_credential_kind: "custom" | "preset";
  api_credential_label: string | null;
  flood_wait_until: string | null;
  last_used_at: string | null;
  last_check_at: string | null;
  last_check_error: string | null;
  warmup_completed: boolean;
  maintenance_enabled?: boolean;
  phone?: string | null;
  tg_2fa_locked_at: string | null;
  total_requests: number;
  total_floodwaits: number;
  total_invites_sent: number;
  is_peer_flood_permanent: boolean;
  peer_flood_until: string | null;
  spam_state?: string | null;
  // When a "limited" verdict lifts on its own, as @SpamBot dated it.
  spam_limited_until?: string | null;
  // When @SpamBot was last asked, what it said, and why it limited the account.
  spam_checked_at?: string | null;
  spam_reply?: string | null;
  spam_reason?: "reports" | "phone_number" | null;
  notes: string | null;
  // Analytics metadata — surfaced on every /api/accounts row.
  source_label: string | null;
  source_url: string | null;
  // Pydantic Decimal serializes to string in JSON.
  purchase_cost_usd: string | null;
  purchase_date: string | null;
  seller_account_age_days: number | null;
  country_code: string | null;
  created_at: string;
  // True when a cached avatar exists → render /api/accounts/{id}/avatar,
  // else colored initials.
  has_avatar?: boolean;
}

export interface Lock2FAResponse {
  locked_at: string;
  password: string;
  hint: string;
}

export interface Lock2FAReveal {
  locked_at: string | null;
  password: string | null;
  hint: string;
}

export function useLock2FA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; current_password?: string }) =>
      api.post<Lock2FAResponse>(`/api/accounts/${vars.id}/lock-2fa`, {
        current_password: vars.current_password || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useReveal2FA() {
  return useMutation({
    mutationFn: (id: string) =>
      api.get<Lock2FAReveal>(`/api/accounts/${id}/2fa-password`),
  });
}

export function useSet2FAPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; password: string }) =>
      api.post<Lock2FAReveal>(`/api/accounts/${vars.id}/2fa-password`, {
        password: vars.password,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Bulk operations on selected accounts
// ---------------------------------------------------------------------------

export interface AccountBulkPatchRequest {
  account_ids: string[];
  notes?: string | null;
  status?: "active" | "paused";
  proxy_id?: string | null;
  proxy_random?: boolean;
  role?: AccountRole;
  source_label?: string | null;
  source_url?: string | null;
  country_code?: string | null;
  inbox_enabled?: boolean;
}

export interface AccountBulkPatchResponse {
  updated: number;
  skipped: { account_id: string; reason: string }[];
}

export function useBulkPatchAccounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AccountBulkPatchRequest) =>
      api.patch<AccountBulkPatchResponse>("/api/accounts/bulk/update", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useBulkDeleteAccounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ deleted: number; skipped: { account_id: string; reason: string }[] }>(
        "/api/accounts/bulk/delete",
        { account_ids: ids },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

/**
 * Pick the status that should be shown in the UI.
 *
 * Storage `status` lags behind reality in two cases the operator cares about:
 * a frozen account whose freeze was caught mid-warmup (still flagged `active`
 * in the row), and an account whose proxy is dead (also `active` — the dead
 * proxy is *off* the account, the account just can't dial out). Derive both
 * here so the badge color matches what the operator actually sees in the
 * detail panel.
 *
 * Genuine non-active statuses (banned, paused, warming_up, flood_wait,
 * auth_required) win over derived states — if TG already banned the account,
 * a dead proxy is the lesser problem.
 */
export function deriveAccountStatus(account: AccountSummary): DerivedStatus {
  const sr = (account.status_reason ?? "").toLowerCase();
  const err = (account.last_check_error ?? "").toLowerCase();
  const isFrozen = sr.includes("frozen") || err.includes("frozen");
  if (isFrozen) return "frozen";
  if (account.status !== "active") return account.status;
  if (proxyUnreachable(account)) return "proxy_dead";
  return account.status;
}

/** The proxy failed its health check, or the last connect died at the network. */
function proxyUnreachable(account: AccountSummary): boolean {
  const err = (account.last_check_error ?? "").toLowerCase();
  return (
    account.proxy_is_alive === false ||
    err.includes("connection to telegram failed") ||
    /^network:/.test(err)
  );
}

/**
 * What currently needs the operator's attention on an account — one issue per
 * account, the most serious first, so the breakdown under "With issues" adds up
 * to its total. Orthogonal to the health score: health rates the account,
 * an issue is something to go and fix now.
 */
export type AccountIssue =
  | "banned"
  | "frozen"
  | "session"
  | "proxy"
  | "flood_wait"
  | "spam_limit"
  | "other";

export const ACCOUNT_ISSUES: AccountIssue[] = [
  "banned",
  "frozen",
  "session",
  "proxy",
  "flood_wait",
  "spam_limit",
  "other",
];

export function deriveAccountIssue(account: AccountSummary): AccountIssue | null {
  const ds = deriveAccountStatus(account);
  if (ds === "banned") return "banned";
  if (ds === "frozen") return "frozen";
  // The deferred verify parks a row whose proxy was taken away as
  // auth_required/proxy_required — the session itself may be fine.
  if (ds === "auth_required") {
    return account.status_reason === "proxy_required" ? "proxy" : "session";
  }
  // No proxy at all counts too: the account would dial Telegram from our IP.
  if (account.proxy_id === null || proxyUnreachable(account)) return "proxy";
  if (ds === "flood_wait") return "flood_wait";
  // A PeerFlood cooldown is the same wall as a @SpamBot limit — no invites or
  // cold DMs until it passes — and used to leave the account looking healthy.
  if (account.spam_state === "limited" || inPeerFloodCooldown(account)) return "spam_limit";
  if (account.is_peer_flood_permanent || account.last_check_error) return "other";
  return null;
}

/** Benched from inviting after a soft PeerFlood, until `peer_flood_until`. */
export function inPeerFloodCooldown(account: AccountSummary): boolean {
  return account.peer_flood_until !== null && Date.parse(account.peer_flood_until) > Date.now();
}

/** "#064" — padded to three digits, wider numbers as they are. */
export function formatAccountNo(seqNo: number): string {
  return `#${String(seqNo).padStart(3, "0")}`;
}

export const ACCOUNTS_KEY = ["accounts"] as const;

// The list endpoint hands out at most this many rows per call. The accounts
// page counts and searches the whole fleet, so read every page — a fleet past
// one page used to lose its oldest accounts (#001…) from the list silently.
const ACCOUNTS_PAGE = 1000;

export function useAccounts() {
  return useQuery<AccountSummary[]>({
    queryKey: ACCOUNTS_KEY,
    queryFn: async () => {
      const all: AccountSummary[] = [];
      for (let offset = 0; ; offset += ACCOUNTS_PAGE) {
        const page = await api.get<AccountSummary[]>(
          `/api/accounts?limit=${ACCOUNTS_PAGE}&offset=${offset}`,
        );
        all.push(...page);
        if (page.length < ACCOUNTS_PAGE) return all;
      }
    },
  });
}

export interface TerminateOtherSessionsResponse {
  status: "terminated" | "delayed_24h" | "none" | "failed";
  terminated_count: number;
  rate_locked_24h: boolean;
  error: string | null;
}

export interface RebalanceProxiesResponse {
  reassigned: number;
  already_matched: number;
  no_match: number;
  no_country: number;
  not_found: number;
  rows: Array<Record<string, string>>;
}

export function useRebalanceProxiesByCountry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (account_ids: string[]) =>
      api.post<RebalanceProxiesResponse>(
        "/api/accounts/bulk/rebalance-proxies-by-country",
        { account_ids },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export interface DistributeProxiesResponse {
  assigned: number;
  not_found: number;
  proxies_used: number;
  /** Proxies left out because their last check failed. */
  dead_proxies_skipped?: number;
  rows: Array<Record<string, string>>;
}

export function useDistributeProxies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { account_ids: string[]; proxy_ids?: string[] }) =>
      api.post<DistributeProxiesResponse>("/api/accounts/bulk/distribute-proxies", vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useTerminateOtherSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<TerminateOtherSessionsResponse>(
        `/api/accounts/${id}/terminate-sessions`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export interface SessionExport {
  tg_user_id: number | null;
  phone: string | null;
  session_string: string;
  api_id: number;
  api_hash: string;
  device_model: string | null;
  system_version: string | null;
  app_version: string | null;
  lang_code: string | null;
}

export function useExportSession() {
  return useMutation({
    mutationFn: (id: string) => api.get<SessionExport>(`/api/accounts/${id}/export-session`),
  });
}

export interface BulkSweepResponse {
  enqueued: number;
  action: string;
  wave_size: number;
}

// Throttled fleet sweep (2FA / terminate-sessions). Runs in waves in the
// background worker; account rows update on their normal refetch.
export function useBulkSweep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      account_ids: string[];
      action: "lock_2fa" | "terminate_sessions";
    }) => api.post<BulkSweepResponse>("/api/accounts/bulk-sweep", vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export interface BulkCheckResponse {
  enqueued: number;
  // Selected, but not connected to Telegram yet — left for their first check.
  skipped_fresh: number;
  eta_seconds: number;
}

export interface BulkCheckStatus {
  active: boolean;
  total: number;
  done: number;
  failed: number;
  skipped: number;
}

const BULK_CHECK_KEY = ["accounts_bulk_check"] as const;

export function bulkCheckProcessed(s: BulkCheckStatus): number {
  return s.done + s.failed + s.skipped;
}

// "Проверить" for many accounts: the worker checks them one at a time, a
// random pause apart. `useBulkCheckStatus` follows it.
export function useBulkCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (account_ids: string[]) =>
      api.post<BulkCheckResponse>("/api/accounts/bulk/check", { account_ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: BULK_CHECK_KEY }),
  });
}

/**
 * Poll while a bulk check runs. A row gets its name and photo only when its own
 * check finishes, so the list is refetched each time the count moves — not on
 * every poll: the list is the whole fleet.
 */
export function useBulkCheckStatus() {
  const qc = useQueryClient();
  return useQuery<BulkCheckStatus>({
    queryKey: BULK_CHECK_KEY,
    queryFn: async () => {
      const next = await api.get<BulkCheckStatus>("/api/accounts/bulk/check");
      const prev = qc.getQueryData<BulkCheckStatus>(BULK_CHECK_KEY);
      if (prev && bulkCheckProcessed(next) !== bulkCheckProcessed(prev)) {
        qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
      }
      return next;
    },
    refetchInterval: (query) => (query.state.data?.active ? 5_000 : false),
  });
}

export function usePatchAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      status?: "active" | "paused";
      role?: "inviter" | "scraper" | "both";
      proxy_id?: string | null;
      notes?: string | null;
      source_label?: string | null;
      source_url?: string | null;
      purchase_cost_usd?: string | number | null;
      purchase_date?: string | null;
      seller_account_age_days?: number | null;
      country_code?: string | null;
    }) =>
      api.patch<AccountSummary>(`/api/accounts/${vars.id}`, {
        status: vars.status,
        role: vars.role,
        proxy_id: vars.proxy_id,
        notes: vars.notes,
        source_label: vars.source_label,
        source_url: vars.source_url,
        purchase_cost_usd: vars.purchase_cost_usd,
        purchase_date: vars.purchase_date,
        seller_account_age_days: vars.seller_account_age_days,
        country_code: vars.country_code,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

// ---------------------------------------------------------------------------
// TG profile edit — first_name / last_name / username / about / avatar.
// Multipart so the avatar can ride along as a real file.
// ---------------------------------------------------------------------------

export interface UpdateTgProfilePayload {
  id: string;
  // Pass undefined to leave field untouched; pass "" to clear it on TG side.
  first_name?: string;
  last_name?: string;
  username?: string;
  about?: string;
  avatar?: File;
  clear_avatar?: boolean;
}

export interface UpdateTgProfileResponse {
  account: AccountSummary;
  username_error: string | null;
  avatar_error: string | null;
}

export function useUpdateTgProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateTgProfilePayload) => {
      const fd = new FormData();
      if (payload.first_name !== undefined) fd.set("first_name", payload.first_name);
      if (payload.last_name !== undefined) fd.set("last_name", payload.last_name);
      if (payload.username !== undefined) fd.set("username", payload.username);
      if (payload.about !== undefined) fd.set("about", payload.about);
      if (payload.clear_avatar) fd.set("clear_avatar", "true");
      if (payload.avatar) fd.set("avatar", payload.avatar);
      const res = await fetch(`/api/accounts/${payload.id}/tg-profile`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const ct = res.headers.get("content-type") ?? "";
      const body: unknown = ct.includes("application/json")
        ? await res.json()
        : await res.text();
      if (!res.ok) {
        throw new ApiError(res.status, extractErrorDetail(body, res.statusText), body);
      }
      return body as UpdateTgProfileResponse;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useTestAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<AccountSummary>(`/api/accounts/${id}/test`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export interface GeneratedProfile {
  first_name: string;
  last_name: string;
  about: string;
  gender: string;
  profession: string;
}

// Template-based profile suggestion (no LLM). Returns values to drop into the
// tg-profile edit form; the operator reviews and saves via useUpdateTgProfile.
export function useGenerateProfile() {
  return useMutation({
    mutationFn: (payload: {
      gender?: string;
      profession?: string;
      lang?: string;
      link?: string;
    }) => api.post<GeneratedProfile>("/api/accounts/generate-profile", payload),
  });
}

// ---------------------------------------------------------------------------
// Per-account events — backend bundles account snapshot + warmup timeline +
// audit-log entries. Surfaced in the UI as a "Скачать логи" button so the
// operator can debug bans locally or hand the JSON off for analysis.
// ---------------------------------------------------------------------------

export interface WarmupActionRow {
  id: string;
  stage: number;
  action_type: string;
  action_params: Record<string, unknown> | null;
  scheduled_at: string;
  executed_at: string | null;
  status: "pending" | "running" | "done" | "failed" | "skipped";
  error: string | null;
  attempts: number;
  created_at: string;
}

export interface AuditEventRow {
  id: string;
  action: string;
  details: Record<string, unknown>;
  actor_user_id: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface InviteAttemptRow {
  id: string;
  campaign_id: string;
  target_channel_title: string | null;
  scraped_user_id: string;
  target_tg_user_id: number;
  status: string;
  error: string | null;
  attempted_at: string | null;
  created_at: string;
}

export interface ScrapeJobRow {
  id: string;
  channel_id: string;
  channel_label: string | null;
  mode: string;
  status: string;
  users_seen: number;
  users_added: number;
  users_updated: number;
  users_skipped: number;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

export interface AccountEventsResponse {
  account_id: string;
  fetched_at: string;
  snapshot: Record<string, unknown>;
  warmup_actions: WarmupActionRow[];
  invite_attempts: InviteAttemptRow[];
  scrape_jobs: ScrapeJobRow[];
  audit_events: AuditEventRow[];
}

export function fetchAccountEvents(id: string): Promise<AccountEventsResponse> {
  return api.get<AccountEventsResponse>(`/api/accounts/${id}/events`);
}

export function useAccountEvents(id: string, enabled: boolean) {
  return useQuery<AccountEventsResponse>({
    queryKey: ["accounts", id, "events"],
    queryFn: () => fetchAccountEvents(id),
    enabled,
    staleTime: 10_000,
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Batch import — the one import path. Takes .zip/.7z archives (one account
// each), loose .session files with their optional <phone>.json companion, and
// session strings pasted as text. The backend normalises all three shapes and
// reports row by row.
// ---------------------------------------------------------------------------

export interface ImportBatchRequest {
  api_credential_id: string;
  // "auto": read api_id/api_hash from each row's own json when it has them,
  // else fall back to the preset above. The operator never has to know which
  // archives carry credentials.
  api_credential_kind: "custom" | "preset" | "from_json" | "auto";
  proxy_id?: string | null;
  proxy_random?: boolean;
  rotate_2fa?: boolean;
  // GramGPT anti-seller schema — early terminate + 2FA, second terminate
  // ~24h later. Opt-in for untrusted sellers; higher freeze risk.
  seller_takeover_defense?: boolean;
  start_warmup?: boolean;
  warmup_speed?: "natural" | "fast" | "aggressive" | "auto";
  proxy_country_match?: boolean;
  // Operator override for the shared-proxy fleet-detection guard: without it
  // the backend refuses a proxy that already carries accounts, including the
  // "importing 2+ sessions onto the same fixed proxy" subcase.
  confirm_shared_proxy?: boolean;
  // Staggered import: persist each account offline and defer its Telegram
  // connect to a per-account jittered job, so a bulk import doesn't light up
  // N fresh logins in one window. Accounts appear as they verify.
  staggered?: boolean;
  source_label?: string;
  source_url?: string;
  purchase_cost_usd?: string | number;
  purchase_date?: string;
  seller_account_age_days?: number;
  country_code?: string;
  // Operator's own note, stored on every account from this batch.
  notes?: string;
  files: File[];
  // Pasted session strings, one per line. Extra columns around them are
  // tolerated, so a seller's text file can be pasted as-is.
  session_strings?: string;
}

export interface ImportBatchLineResult {
  // Archive filename, `<phone>.session`, or `pasted-N` for a pasted line.
  archive_name: string;
  // "queued" = staggered import persisted the account offline; its verify
  // connect runs later in a per-account jittered job (see verify_import_job).
  status: "ok" | "error" | "queued";
  account_id: string | null;
  tg_user_id: number | null;
  tg_username: string | null;
  reason: string | null;
  detail: string | null;
  twofa_status:
    | "rotated"
    | "set_fresh"
    | "kept_seller"
    | "kept_unknown"
    | "set_fresh_failed"
    | null;
  twofa_password: string | null;
  other_sessions_status:
    | "terminated"
    | "delayed_24h"
    | "none"
    | "skipped"
    | "failed"
    | null;
  other_sessions_terminated_count: number | null;
}

export interface ImportBatchResponse {
  total: number;
  imported: number;
  failed: number;
  results: ImportBatchLineResult[];
}

export function useImportBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ImportBatchRequest) => {
      const fd = new FormData();
      fd.set("api_credential_id", payload.api_credential_id);
      fd.set("api_credential_kind", payload.api_credential_kind);
      if (payload.proxy_id) fd.set("proxy_id", payload.proxy_id);
      if (payload.proxy_random !== undefined)
        fd.set("proxy_random", payload.proxy_random ? "true" : "false");
      if (payload.rotate_2fa !== undefined)
        fd.set("rotate_2fa", payload.rotate_2fa ? "true" : "false");
      if (payload.seller_takeover_defense)
        fd.set("seller_takeover_defense", "true");
      if (payload.start_warmup !== undefined)
        fd.set("start_warmup", payload.start_warmup ? "true" : "false");
      if (payload.warmup_speed)
        fd.set("warmup_speed", payload.warmup_speed);
      if (payload.proxy_country_match !== undefined)
        fd.set(
          "proxy_country_match",
          payload.proxy_country_match ? "true" : "false",
        );
      if (payload.source_label) fd.set("source_label", payload.source_label);
      if (payload.source_url) fd.set("source_url", payload.source_url);
      if (payload.purchase_cost_usd !== undefined && payload.purchase_cost_usd !== "") {
        fd.set("purchase_cost_usd", String(payload.purchase_cost_usd));
      }
      if (payload.purchase_date) fd.set("purchase_date", payload.purchase_date);
      if (payload.seller_account_age_days !== undefined) {
        fd.set("seller_account_age_days", String(payload.seller_account_age_days));
      }
      if (payload.country_code) fd.set("country_code", payload.country_code);
      if (payload.confirm_shared_proxy) fd.set("confirm_shared_proxy", "true");
      if (payload.staggered) fd.set("staggered", "true");
      if (payload.notes) fd.set("notes", payload.notes);
      if (payload.session_strings) fd.set("session_strings", payload.session_strings);
      for (const f of payload.files) fd.append("files", f);
      const res = await fetch("/api/accounts/import-batch", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const ct = res.headers.get("content-type") ?? "";
      const body: unknown = ct.includes("application/json")
        ? await res.json()
        : await res.text();
      if (!res.ok) {
        throw new ApiError(res.status, extractErrorDetail(body, res.statusText), body);
      }
      return body as ImportBatchResponse;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

// ---------------------------------------------------------------------------
// `status_reason` in words
// ---------------------------------------------------------------------------

// The backend writes the reason as a code, or as a code followed by an English
// explanation. Shown as is it read "Paused by operator" under the label "last
// check error", and a system pause looked the same as a manual one. Matched by
// prefix; an unknown reason falls back to the raw text rather than to nothing.
const STATUS_REASON_KEYS: ReadonlyArray<readonly [prefix: string, key: string]> = [
  ["Paused by operator", "paused_by_operator"],
  ["duplicate_tg_user_id", "duplicate"],
  ["verify_orphaned", "verify_orphaned"],
  ["warmup_plan_failed", "warmup_plan_failed"],
  ["warmup_aborted", "warmup_aborted"],
  ["seller_eviction_unverified", "seller_eviction_unverified"],
  ["reauth_failed", "reauth_failed"],
  ["proxy_required", "proxy_required"],
  ["auth_duplicated", "auth_duplicated"],
  ["password_needed", "password_needed"],
  ["auth_revoked", "auth_revoked"],
  ["phone_banned", "phone_banned"],
  ["deactivated", "deactivated"],
  ["frozen", "frozen"],
];

export function describeStatusReason(reason: string, t: TFunction): string {
  const hit = STATUS_REASON_KEYS.find(([prefix]) => reason.startsWith(prefix));
  return hit ? t(`accounts.status_reason.${hit[1]}`) : reason;
}
